import { Market } from '../context/claims';
import { loadDevOverrides } from './devOverrides';

export function getCustomSchemeUrl(token: string): string {
  return env.VITE_CUSTOMSCHEME_PREFIX + token;
}

export function getAndroidIntentUrl(currentUrl: URL): string {
  // See https://developer.chrome.com/docs/android/intents
  const url = new URL(currentUrl.href);
  url.searchParams.set('redirectAttempted', 'true');

  return `intent://${url.host}${url.pathname}${url.search}` + `#Intent;scheme=https;end`;
}

export function getCookieDomain(): string {
  const hostname = window.location.hostname;

  // localhost / raw IPs can't carry a leading-dot domain cookie — keep them host-scoped.
  if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return hostname;
  }

  // Use the registrable domain (eTLD+1) with a leading dot so the cookie is shared across all
  // subdomains of that parent. This is what makes the cross-domain remember-me cookie reachable when
  // different merchants embed the Vipps iframe from different subdomains of the same parent
  // (e.g. *.vipps.no, *.mobilepay.dk, *.tech-02.net). None of our hosts use a multi-part eTLD
  // (.co.uk etc.), so the last two labels are the registrable domain.
  const labels = hostname.split('.');
  const registrableDomain = labels.length <= 2 ? hostname : labels.slice(-2).join('.');
  return `.${registrableDomain}`;
}

export function getUniversalLinkingUrl(token: string, market: Market, currentUrl: URL): string {
  switch (market) {
    case Market.NO:
      return getPayDotVippsLPUrl(token, currentUrl);
    default:
      return doTheOldSwitcheroo(currentUrl);
  }
}

export function doTheOldSwitcheroo(currentUrl: URL): string {
  //switch .fi and .dk to be able to trigger universal linking, and hopefully without confusing the user too much if it doesnt work
  const url = new URL(currentUrl.href);
  const parts = url.hostname.split('.');
  const topLevelDomain = parts[parts.length - 1];

  switch (topLevelDomain) {
    case 'dk':
      parts[parts.length - 1] = 'fi';
      break; // Add break statement here
    default:
      parts[parts.length - 1] = 'dk';
  }

  url.hostname = parts.join('.');
  url.searchParams.append('redirectAttempted', 'true');

  return url.href;
}

function getPayDotVippsLPUrl(token: string, currentUrl: URL): string {
  const lpUrl = env.VITE_LP_FRONTEND_URL.replace('${token}', token);

  // A same-domain universal link won't trigger the app switch, so bounce the payer to the other
  // domain to force a cross-domain navigation: from the primary (LP) domain to the deeplink
  // gateway, and from anywhere else (including the gateway) back to the primary domain.
  if (currentUrl.host === new URL(lpUrl).host) {
    const gatewayUrl = new URL(env.VITE_LP_SECONDARY_URL);
    gatewayUrl.search = currentUrl.search;
    gatewayUrl.searchParams.set('v', '2');
    gatewayUrl.searchParams.set('token', token);
    gatewayUrl.searchParams.set('redirectAttempted', 'true');
    return gatewayUrl.href;
  }

  return lpUrl;
}

/***
@description Casts strings to other primitive types i.e null, number and boolean. 
**/

const castEnvValue = (input: unknown) => {
  // Vite exposes some keys as booleans (e.g. PROD, DEV); VITE_* are strings.
  if (typeof input !== 'string') {
    return input;
  }

  const str = String(input ?? '');
  const lowInput = str.toLowerCase();

  if (['true', 'false'].includes(lowInput)) {
    return lowInput === 'true';
  }

  if (['', 'null', 'undefined'].includes(lowInput)) {
    return null;
  }

  if (!Number.isNaN(Number(str))) {
    return Number(str);
  }

  return str;
};

// Only this module may read `import.meta.env`; elsewhere `ImportMeta.env` is typed as `never` (see vite-env.d.ts).
const rawImportMetaEnv = import.meta.env as unknown as ImportMetaEnv;

const baseEnv = Object.entries(rawImportMetaEnv).reduce(
  (acc, [k, v]) => {
    if (!k.startsWith('VITE_')) {
      return acc;
    }

    return {
      ...acc,
      [k]: castEnvValue(v),
    };
  },
  {
    BASE_URL: rawImportMetaEnv.BASE_URL,
    MODE: rawImportMetaEnv.MODE,
    DEV: rawImportMetaEnv.MODE === 'dev',
    UAT: rawImportMetaEnv.MODE === 'uat',
    MT: rawImportMetaEnv.MODE === 'mt',
    PROD: rawImportMetaEnv.MODE === 'prod',
  } as TypedImportMetaEnv
);

// Dev menu can override VITE_* config values (persisted in localStorage, applied at load).
// Gated to non-prod so a prod build never reads the override store.
const applyDevConfigOverrides = (base: TypedImportMetaEnv): TypedImportMetaEnv => {
  if (base.MODE === 'prod') {
    return base;
  }
  const overrides = loadDevOverrides().config;
  if (!overrides) {
    return base;
  }
  const applied = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(overrides)) {
    if (k in applied) {
      applied[k] = castEnvValue(v);
    }
  }
  return applied as TypedImportMetaEnv;
};

export const env = applyDevConfigOverrides(baseEnv);
